/*
 * Instants in, readable local time out. No Vue, no fetch.
 *
 * The API stores and answers UTC instants; every screen renders them in the reader's own zone,
 * because "when does the match start" is a wall-clock question. A host in Johannesburg setting
 * 15:00 and a player in Chicago seeing 08:00 is the correct answer, not a bug.
 *
 * `locale` and `zone` are parameters rather than ambient reads. STATUS.md records a locale-derived
 * default that shipped green through `pnpm verify` and broke two Playwright specs, because the
 * unit test stubbed a navigator and the e2e run could not — so nothing here reads the environment
 * unless the caller declines to say.
 */

const OPTIONS: Intl.DateTimeFormatOptions = {
  dateStyle: 'medium',
  timeStyle: 'short',
};

/** A real instant, or null. Anything off the wire is suspect until it parses. */
function instant(value: string | null | undefined): Date | null {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * A date and time for reading, in the given zone — or null when there is nothing to show, so the
 * caller can skip the whole line rather than printing "Invalid Date" at somebody.
 */
export function formatDateTime(
  value: string | null | undefined,
  locale?: string,
  zone?: string,
): string | null {
  const parsed = instant(value);
  if (!parsed) return null;

  return new Intl.DateTimeFormat(locale, {
    ...OPTIONS,
    ...(zone ? { timeZone: zone } : {}),
  }).format(parsed);
}

/**
 * The value `<input type="datetime-local">` expects: local wall-clock, no zone, no seconds.
 *
 * Built from the formatted parts rather than by string-slicing an ISO string, because slicing
 * gives UTC — which would show a host in Johannesburg a time two hours off what they set.
 */
export function toLocalInput(value: string | null | undefined, zone?: string): string {
  const parsed = instant(value);
  if (!parsed) return '';

  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...(zone ? { timeZone: zone } : {}),
  }).formatToParts(parsed);

  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((candidate) => candidate.type === type)?.value ?? '';

  // Midnight comes back as hour 24 in some environments, which is not a value the input accepts.
  const hour = part('hour') === '24' ? '00' : part('hour');

  return `${part('year')}-${part('month')}-${part('day')}T${hour}:${part('minute')}`;
}

/**
 * The reverse: what a `datetime-local` field holds, as a UTC instant for the API.
 *
 * `new Date('2026-09-05T15:00')` — no zone — is parsed as *local* time by every browser, which is
 * exactly the interpretation wanted here: the host typed a wall-clock time in their own zone.
 */
export function fromLocalInput(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;

  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}
